import { useState } from "react";
import {
  ListItem,
  ListItemAvatar,
  ListItemText,
  Typography,
  MenuItem,
  Tooltip,
  IconButton,
  Popover,
  ListItemIcon,
  Box,
} from "@mui/material";
import CopyLinkButton from "./CopyLinkButton";
import AvatarImage from "./AvatarImage";
import { IMAGE_STATE } from "types/imageState";
import { useFilter } from "hooks/filter";
import { useMediaModal } from "hooks/mediaModal";
import { Media } from "types/qbank/media";
import { getPublishUrl } from "utilities/mediaUrl";
import { DeploymentSite } from "types/qbank/deploymentSite";
import AspectRatioIcon from "@mui/icons-material/AspectRatio";
import Check from "@mui/icons-material/Check";

type IProps = {
  media: Media;
  deploymentSite: DeploymentSite;
};

const MediaList = ({ media, deploymentSite }: IProps) => {
  const { searchState } = useFilter();
  const { setModal } = useMediaModal();
  const [templateId, setTemplateId] = useState<number>(
    parseInt(searchState.currentCategory.imageTemplateId)
  );
  const publishUrl = getPublishUrl(deploymentSite, media.deployedFiles, templateId);
  const availableTemplates = media.deployedFiles?.filter(
    (deployedFile) =>
      deployedFile.imageTemplateId !== null &&
      deployedFile.deploymentSiteId === deploymentSite.id
  );
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };
  const open = Boolean(anchorEl);
  const id = open ? "simple-popover" : undefined;

  return (
    <ListItem
      key={media.mediaId}
      disablePadding
      disableGutters
      alignItems="flex-start"
      sx={{ marginBottom: '12px', gap: 1, width: '100%', maxWidth: '94vw' }}
      secondaryAction={
        <>
          <Box
            sx={{
              display: "flex",
              gap: "8px",
            }}
          >
          {availableTemplates && availableTemplates.length > 0 && (
            <>
                <Box
                  sx={{
                    border: '1.5px solid #C6D4DC',
                    borderRadius: '8px',
                    display: 'flex',
                    alignItems: 'center',
                    maxWidth: '30vw',
                    overflow: 'hidden',
                    whiteSpace: 'nowrap',
                    textOverflow: 'ellipsis',
                    '&:hover': {
                      backgroundColor: '#023E58',
                      color: 'white'
                    },
                  }}

                >
                  <Tooltip title="change template">
                    <IconButton aria-label="change template" onClick={handleClick} sx={{
                      height: '28px',
                      width: '28px',
                      color: 'inherit',
                    }}>
                      <AspectRatioIcon />
                    </IconButton>
                  </Tooltip>
              <Typography
                    variant="caption"
                    color="inherit"
                    noWrap={true}
                    onClick={handleClick}
                    textOverflow="ellipsis"
                    sx={{ mr: 1, overflow: 'hidden' }}
              >
                {
                  availableTemplates.find(
                    (template) => template.imageTemplateId === templateId
                  )?.templateName
                }
                  </Typography>
                </Box>
              <Popover
                id={id}
                open={open}
                anchorEl={anchorEl}
                onClose={handleClose}
                anchorOrigin={{
                  vertical: "bottom",
                  horizontal: "left",
                }}
              >
                {availableTemplates.map((deployedFile, i) => {
                  const active = templateId === deployedFile.imageTemplateId;
                  return (
                    <MenuItem
                      dense
                      divider
                      key={i}
                      value={deployedFile.imageTemplateId || 1}
                      onClick={() => {
                        setTemplateId(deployedFile.imageTemplateId || 1);
                        handleClose();
                      }}
                    >
                      {active && (
                        <ListItemIcon>
                          <Check />
                        </ListItemIcon>
                      )}
                      <ListItemText inset={active ? false : true}>
                        {deployedFile.templateName ||
                          JSON.parse(deployedFile.metadata)["templateName"]}
                      </ListItemText>
                    </MenuItem>
                  );
                })}
              </Popover>

            </>
          )}
            <Box
              sx={{
                border: '1.5px solid #C6D4DC',
                borderRadius: '8px',
                display: 'flex',
                alignItems: 'center',
                '&:hover': {
                  backgroundColor: '#023E58',
                  color: 'white'
                },
              }}
            >
          <CopyLinkButton
            media={media}
            text={publishUrl || ""}
            imageState={IMAGE_STATE.PRIMARY}
            disabled={false}
                sx={{
                  height: '28px',
                  width: '28px',
                  color: "inherit"
                }}
          />
            </Box>
          </Box>
        </>
      }
    >

      <ListItemAvatar onClick={() => setModal({ open: true, media })}>
          <AvatarImage alt={`${media.filename}`} src={publishUrl || ""} />
      </ListItemAvatar>
      <Box
        sx={{
          overflow: "hidden",
          display: "block",
          maxWidth: {
            xs: "40%",
            sm: "60%",
            md: "70%",
          }
        }}>
        <Tooltip title={media.name}>
        <ListItemText

          inset={false}
          primary={media.name}
          primaryTypographyProps={{ sx: { whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" } }}
        secondary={
          <Typography
            sx={{ display: "inline" }}
            component="span"
            variant="body2"
            noWrap
            color="text.primary"
          >
            {media.mediaId}
          </Typography>
        }
      />
        </Tooltip>
      </Box>
    </ListItem>
  );
};

export default MediaList;
